import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, ExternalLink, Copy, Check, Globe, Key, Lock, Clock, AlertTriangle } from 'lucide-react';

interface IntegrationsDocumentationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const IntegrationsDocumentationModal: React.FC<IntegrationsDocumentationModalProps> = ({
  isOpen,
  onClose
}) => {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const integrations = [
    {
      id: 'mercadolibre',
      name: 'Mercado Libre',
      icon: '🛒',
      authType: 'OAuth 2.0',
      documentation: {
        overview: 'Mercado Libre utiliza OAuth 2.0 para autenticación. Necesitarás crear una aplicación en el portal de desarrolladores.',
        steps: [
          'Visita https://developers.mercadolibre.com/',
          'Crea una nueva aplicación',
          'Configura los redirect URIs',
          'Obtén tu Client ID y Client Secret',
          'Usa el flujo OAuth para obtener tokens'
        ],
        credentials: {
          required: ['client_id', 'client_secret'],
          optional: ['access_token', 'refresh_token']
        },
        limits: {
          requestsPerMinute: 60,
          requestsPerHour: 1000,
          notes: 'Los límites varían según el tipo de aplicación'
        },
        codeExample: `// Ejemplo de configuración OAuth
const mercadolibreConfig = {
  client_id: 'tu_client_id',
  client_secret: 'tu_client_secret',
  redirect_uri: 'https://tu-app.com/callback',
  scope: 'read_products write_products'
};`
      }
    },
    {
      id: 'amazon',
      name: 'Amazon Marketplace',
      icon: '📦',
      authType: 'OAuth 2.0',
      documentation: {
        overview: 'Amazon utiliza el Selling Partner API con autenticación OAuth 2.0. Requiere aprobación previa.',
        steps: [
          'Regístrate en Seller Central',
          'Solicita acceso al Selling Partner API',
          'Crea una aplicación IAM',
          'Configura los permisos necesarios',
          'Implementa el flujo OAuth'
        ],
        credentials: {
          required: ['client_id', 'client_secret', 'refresh_token'],
          optional: ['access_token']
        },
        limits: {
          requestsPerMinute: 30,
          requestsPerHour: 500,
          notes: 'Límites estrictos, requiere planificación de requests'
        },
        codeExample: `// Configuración Amazon Selling Partner API
const amazonConfig = {
  client_id: 'tu_client_id',
  client_secret: 'tu_client_secret',
  refresh_token: 'tu_refresh_token',
  marketplace_id: 'A1PA6795UKMFR9',
  region: 'eu-west-1'
};`
      }
    },
    {
      id: 'shopify',
      name: 'Shopify',
      icon: '🛍️',
      authType: 'OAuth 2.0',
      documentation: {
        overview: 'Shopify utiliza OAuth 2.0 con aplicaciones privadas o públicas. Las aplicaciones privadas usan API keys.',
        steps: [
          'Crea una aplicación en Shopify Partners',
          'Configura los redirect URIs',
          'Define los scopes de acceso',
          'Obtén las credenciales',
          'Implementa el flujo de instalación'
        ],
        credentials: {
          required: ['shop_domain', 'access_token'],
          optional: ['api_key', 'api_secret']
        },
        limits: {
          requestsPerMinute: 40,
          requestsPerHour: 800,
          notes: 'Límites por tienda, no globales'
        },
        codeExample: `// Configuración Shopify
const shopifyConfig = {
  shop_domain: 'tu-tienda.myshopify.com',
  access_token: 'tu_access_token',
  api_version: '2023-10'
};`
      }
    },
    {
      id: 'tiendanube',
      name: 'Tiendanube',
      icon: '☁️',
      authType: 'OAuth 2.0',
      documentation: {
        overview: 'Tiendanube utiliza OAuth 2.0 para aplicaciones. Requiere registro como desarrollador.',
        steps: [
          'Regístrate en https://developers.tiendanube.com/',
          'Crea una nueva aplicación',
          'Configura los permisos',
          'Obtén Client ID y Secret',
          'Implementa el flujo OAuth'
        ],
        credentials: {
          required: ['client_id', 'client_secret'],
          optional: ['access_token', 'refresh_token']
        },
        limits: {
          requestsPerMinute: 50,
          requestsPerHour: 1000,
          notes: 'Límites generosos para desarrolladores'
        },
        codeExample: `// Configuración Tiendanube
const tiendanubeConfig = {
  client_id: 'tu_client_id',
  client_secret: 'tu_client_secret',
  store_id: 'tu_store_id'
};`
      }
    },
    {
      id: 'prestashop',
      name: 'PrestaShop',
      icon: '⚡',
      authType: 'API Key',
      documentation: {
        overview: 'PrestaShop utiliza API Keys para autenticación. Se configura directamente en el panel de administración.',
        steps: [
          'Accede al panel de administración',
          'Ve a Avanzado > Web Services',
          'Crea una nueva API Key',
          'Configura los permisos',
          'Usa la API Key en las requests'
        ],
        credentials: {
          required: ['domain', 'api_key'],
          optional: []
        },
        limits: {
          requestsPerMinute: 60,
          requestsPerHour: 1000,
          notes: 'Límites configurables en el servidor'
        },
        codeExample: `// Configuración PrestaShop
const prestashopConfig = {
  domain: 'https://tu-tienda.com',
  api_key: 'tu_api_key'
};`
      }
    },
    {
      id: 'magento',
      name: 'Magento',
      icon: '🏪',
      authType: 'OAuth 2.0',
      documentation: {
        overview: 'Magento 2 utiliza OAuth 2.0 para aplicaciones. Requiere configuración en el backend.',
        steps: [
          'Accede al panel de administración',
          'Ve a Sistema > Extensiones > Integrations',
          'Crea una nueva integración',
          'Configura los recursos',
          'Obtén las credenciales OAuth'
        ],
        credentials: {
          required: ['domain', 'client_id', 'client_secret'],
          optional: ['access_token', 'refresh_token']
        },
        limits: {
          requestsPerMinute: 60,
          requestsPerHour: 1000,
          notes: 'Límites configurables por integración'
        },
        codeExample: `// Configuración Magento 2
const magentoConfig = {
  domain: 'https://tu-tienda.com',
  client_id: 'tu_client_id',
  client_secret: 'tu_client_secret'
};`
      }
    },
    {
      id: 'wix',
      name: 'Wix',
      icon: '🎨',
      authType: 'OAuth 2.0',
      documentation: {
        overview: 'Wix utiliza OAuth 2.0 para aplicaciones. Requiere registro en Wix Developers.',
        steps: [
          'Regístrate en https://dev.wix.com/',
          'Crea una nueva aplicación',
          'Configura los permisos',
          'Obtén las credenciales',
          'Implementa el flujo OAuth'
        ],
        credentials: {
          required: ['client_id', 'client_secret'],
          optional: ['access_token', 'refresh_token']
        },
        limits: {
          requestsPerMinute: 60,
          requestsPerHour: 1000,
          notes: 'Límites por aplicación'
        },
        codeExample: `// Configuración Wix
const wixConfig = {
  client_id: 'tu_client_id',
  client_secret: 'tu_client_secret',
  site_id: 'tu_site_id'
};`
      }
    },
    {
      id: 'bigcommerce',
      name: 'BigCommerce',
      icon: '🏢',
      authType: 'OAuth 2.0',
      documentation: {
        overview: 'BigCommerce utiliza OAuth 2.0 para aplicaciones. Requiere registro como desarrollador.',
        steps: [
          'Regístrate en https://developer.bigcommerce.com/',
          'Crea una nueva aplicación',
          'Configura los scopes',
          'Obtén las credenciales',
          'Implementa el flujo OAuth'
        ],
        credentials: {
          required: ['store_hash', 'client_id', 'client_secret'],
          optional: ['access_token', 'refresh_token']
        },
        limits: {
          requestsPerMinute: 60,
          requestsPerHour: 1000,
          notes: 'Límites por tienda'
        },
        codeExample: `// Configuración BigCommerce
const bigcommerceConfig = {
  store_hash: 'tu_store_hash',
  client_id: 'tu_client_id',
  client_secret: 'tu_client_secret'
};`
      }
    },
    {
      id: 'vtex',
      name: 'VTEX',
      icon: '🌐',
      authType: 'API Key',
      documentation: {
        overview: 'VTEX utiliza API Keys para autenticación. Se configuran en el portal de desarrolladores.',
        steps: [
          'Accede al portal VTEX',
          'Ve a Account Settings > API Keys',
          'Crea una nueva API Key',
          'Configura los permisos',
          'Usa la API Key en las requests'
        ],
        credentials: {
          required: ['account', 'api_key'],
          optional: ['api_token']
        },
        limits: {
          requestsPerMinute: 60,
          requestsPerHour: 1000,
          notes: 'Límites configurables por cuenta'
        },
        codeExample: `// Configuración VTEX
const vtexConfig = {
  account: 'tu_account',
  api_key: 'tu_api_key',
  environment: 'vtexcommercestable'
};`
      }
    }
  ];

  const copyToClipboard = async (text: string, codeId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(codeId);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  const getAuthIcon = (authType: string) => {
    switch (authType) {
      case 'OAuth 2.0':
        return <Globe className="w-4 h-4 text-blue-500" />;
      case 'API Key':
        return <Key className="w-4 h-4 text-green-500" />;
      default:
        return <Lock className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  Documentación de Integraciones
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Guías técnicas para configurar cada plataforma
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="space-y-6">
                {integrations.map((integration) => (
                  <motion.div
                    key={integration.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden"
                  >
                    {/* Integration Header */}
                    <div className="p-4 bg-gray-50 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl">{integration.icon}</span>
                          <div>
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                              {integration.name}
                            </h3>
                            <div className="flex items-center space-x-2 mt-1">
                              {getAuthIcon(integration.authType)}
                              <span className="text-sm text-gray-600 dark:text-gray-300">
                                {integration.authType}
                              </span>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => window.open(`https://developers.${integration.id}.com`, '_blank')}
                          className="flex items-center space-x-1 px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors duration-200"
                        >
                          <ExternalLink className="w-3 h-3" />
                          <span className="text-xs font-medium">Docs</span>
                        </button>
                      </div>
                    </div>

                    {/* Integration Content */}
                    <div className="p-4 space-y-4">
                      {/* Overview */}
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                          Descripción
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {integration.documentation.overview}
                        </p>
                      </div>

                      {/* Steps */}
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                          Pasos de configuración
                        </h4>
                        <ol className="list-decimal list-inside space-y-1">
                          {integration.documentation.steps.map((step, index) => (
                            <li key={index} className="text-sm text-gray-600 dark:text-gray-300">
                              {step}
                            </li>
                          ))}
                        </ol>
                      </div>

                      {/* Credentials */}
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                          Credenciales requeridas
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h5 className="text-sm font-medium text-green-700 dark:text-green-300 mb-1">
                              Requeridas
                            </h5>
                            <div className="flex flex-wrap gap-1">
                              {integration.documentation.credentials.required.map((cred) => (
                                <span
                                  key={cred}
                                  className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs"
                                >
                                  {cred}
                                </span>
                              ))}
                            </div>
                          </div>
                          {integration.documentation.credentials.optional.length > 0 && (
                            <div>
                              <h5 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                                Opcionales
                              </h5>
                              <div className="flex flex-wrap gap-1">
                                {integration.documentation.credentials.optional.map((cred) => (
                                  <span
                                    key={cred}
                                    className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs"
                                  >
                                    {cred}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Limits */}
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                          Límites de API
                        </h4>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                              {integration.documentation.limits.requestsPerMinute} req/min
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                              {integration.documentation.limits.requestsPerHour} req/hora
                            </span>
                          </div>
                        </div>
                        {integration.documentation.limits.notes && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {integration.documentation.limits.notes}
                          </p>
                        )}
                      </div>

                      {/* Code Example */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-900 dark:text-gray-100">
                            Ejemplo de configuración
                          </h4>
                          <button
                            onClick={() => copyToClipboard(integration.documentation.codeExample, integration.id)}
                            className="flex items-center space-x-1 px-2 py-1 text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                          >
                            {copiedCode === integration.id ? (
                              <>
                                <Check className="w-3 h-3" />
                                <span>Copiado</span>
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3" />
                                <span>Copiar</span>
                              </>
                            )}
                          </button>
                        </div>
                        <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg overflow-x-auto">
                          <code className="text-sm text-gray-800 dark:text-gray-200">
                            {integration.documentation.codeExample}
                          </code>
                        </pre>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Footer */}
              <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900 dark:text-blue-100">
                      Importante
                    </h4>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                      Mantén tus credenciales seguras y nunca las compartas. Z.AI encripta y almacena 
                      todas las credenciales de forma segura en tu cuenta personal.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default IntegrationsDocumentationModal;
